import React, { useMemo } from 'react';
import type { ImageModelDto, ImageModelMode } from '@api/processes';
import { useImageModels } from '../../hooks/useImageModels';
import SelectField, { type SelectFieldOption } from './SelectField';
import { formatCreditCost } from './imageModelOptions';
import { TooltipBody, TooltipPrice } from './ImageModelSelect.styled';

/** Empty value = let the service pick its default model. */
const DEFAULT_OPTION: SelectFieldOption = {
  value: '',
  label: 'Default (recommended)',
  description: 'Use the built-in model. Pick a specific one below to override.',
};

/** Tooltip: description, then (on its own line, italicised) the per-image cost. */
const toOption = (m: ImageModelDto): SelectFieldOption => {
  const description = m.description?.trim();
  const price = formatCreditCost(m.creditCost, m.referenceCreditCost);
  return {
    value: m.id,
    label: m.displayName || m.id,
    description: description || price || undefined,
    descriptionNode:
      description || price ? (
        <TooltipBody>
          {description ? <span>{description}</span> : null}
          {price ? <TooltipPrice>{price}</TooltipPrice> : null}
        </TooltipBody>
      ) : undefined,
  };
};

export interface ImageModelSelectProps {
  /** generate (text-to-image) when nothing is wired in, edit otherwise. */
  readonly mode: ImageModelMode;
  /** Currently persisted fal endpoint id; blank means the default. */
  readonly value: string | undefined;
  readonly onChange: (modelId: string) => void;
  readonly readOnly?: boolean;
  readonly testId: string;
}

/**
 * Model dropdown for the Image Generation node. Loads the curated fal.ai
 * catalog for the active {@code mode} and always offers a leading "Default"
 * option so the node can fall back to the service default (chat behaviour).
 */
const ImageModelSelect: React.FC<ImageModelSelectProps> = ({
  mode,
  value,
  onChange,
  readOnly,
  testId,
}) => {
  const { data: models } = useImageModels(mode);

  const options = useMemo<SelectFieldOption[]>(() => {
    return [DEFAULT_OPTION, ...(models ?? []).map(toOption)];
  }, [models]);

  return (
    <SelectField
      label="Model"
      options={options}
      value={value ?? ''}
      onChange={onChange}
      readOnly={readOnly}
      ariaLabel={mode === 'edit' ? 'Image edit model' : 'Image generation model'}
      testId={testId}
      searchable
      searchPlaceholder="Search models…"
    />
  );
};

export default ImageModelSelect;
